// Backend-agnostic identity contract. `NetworkProvider`, `UserContext`,
// `LoginPage`, and `useLogoutWithO11y` all consume `useIdentity()` and never
// know whether the app is running in a browser (Auth0) or embedded in a
// native WebView (postMessage bridge to native).
//
// The Auth0 backend is loaded via `React.lazy` so `@auth0/auth0-react` never
// ships in the WebView bundle: the only runtime import of that package lives
// in `./auth0Identity`, which this module reaches exclusively through
// `import()`. Everything imported here from `@auth0/auth0-react` is
// `import type`, which TypeScript erases — zero bundle cost.

import React, { Suspense, useContext } from 'react';
import type { PropsWithChildren } from 'react';
import type { Auth0ProviderOptions } from '@auth0/auth0-react';
import { isEmbeddedWebView } from '../api/network/nativeBridge';

export interface IdentityUser {
  userId: string;
  email?: string;
  name?: string;
  picture?: string;
  clientId?: string;
  homeTenant?: string;
  roleId?: string;
  targetTenant?: string;
  [key: string]: unknown;
}

export interface IdentityState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: IdentityUser | null;
  error?: Error;
  getAccessToken: (options?: {
    authorizationParams?: Record<string, string>;
  }) => Promise<string>;
  loginWithRedirect: (options?: {
    appState?: { returnTo: string };
    authorizationParams?: Record<string, string>;
  }) => Promise<void>;
  logout: (options?: { logoutParams?: { returnTo?: string } }) => Promise<void>;
}

export const IdentityContext = React.createContext<IdentityState | null>(null);

export function useIdentity(): IdentityState {
  const identity = useContext(IdentityContext);
  if (!identity) {
    throw new Error(
      'IdentityContext is not available. Wrap the app with IdentityProvider from src/auth'
    );
  }
  return identity;
}

const IN_WEBVIEW = isEmbeddedWebView();

// Lazy: only imported (and only pulls in @auth0/auth0-react) when rendered,
// which happens exclusively on the browser branch below.
const LazyAuth0IdentityProvider = React.lazy(() =>
  import('./auth0Identity').then(module => ({
    default: module.Auth0IdentityProvider,
  }))
);

// Lazy for symmetry and to keep this always-eager module free of any
// transport-specific state; native mode has no heavy dependency, so the
// split has no bundle-size motivation, only architectural consistency.
const LazyNativeIdentityProvider = React.lazy(() =>
  import('./nativeIdentity').then(module => ({
    default: module.NativeIdentityProvider,
  }))
);

export function IdentityProvider(
  props: PropsWithChildren<Auth0ProviderOptions>
) {
  const { children, ...providerProps } = props;
  if (IN_WEBVIEW) {
    return React.createElement(
      Suspense,
      { fallback: null },
      React.createElement(LazyNativeIdentityProvider, null, children)
    );
  }
  return React.createElement(
    Suspense,
    { fallback: null },
    React.createElement(LazyAuth0IdentityProvider, providerProps, children)
  );
}
